import { call, put, takeLatest } from 'redux-saga/effects';

import { getErrorMessage } from 'utils/errorUtils';
import { getLogger } from 'utils/logger';

const logger = getLogger('GroupDetail.saga');

import {
	addNewClientAPI,
	addSessionAPI,
	deleteSessionsAPI,
	fetchAllClientsAPI,
	fetchAttendanceAPI,
	fetchClientsAPI,
	fetchMyClientsAPI,
	fetchSessionsAPI,
	getProgramAPI,
	saveAttendanceAPI,
	updateEnrollmentApi,
	sendSurveyAPI,
} from 'utils/api';
import { noop } from 'utils/common';

import {
	addClientError,
	addClientSuccess,
	addSessionError,
	addSessionSuccess,
	deleteSessionsError,
	deleteSessionsSuccess,
	fetchAllClientsError,
	fetchAllClientsSuccess,
	getAttendanceError,
	getAttendanceSuccess,
	getClients as getClientsAction,
	getClientsError,
	getClientsSuccess,
	getSessionsError,
	getSessionsSuccess,
	loadProgram,
	loadProgramError,
	loadProgramSuccess,
	saveAttendanceError,
	saveAttendanceSuccess,
	updateEnrollmentError,
	updateEnrollmentSuccess,
} from './actions';
import {
	ADD_CLIENT,
	ADD_SESSION,
	DELETE_SESSIONS,
	FETCH_ALL_CLIENTS,
	GET_ATTENDANCE,
	GET_CLIENTS,
	GET_SESSIONS,
	LOAD_PROGRAM,
	SAVE_ATTENDANCE,
	SEND_SURVEY,
	UPDATE_ENROLLMENT,
} from './constants';

export default function* groupDetailSaga() {
	yield takeLatest(GET_ATTENDANCE, getAttendance);
	yield takeLatest(ADD_SESSION, addNewSession);
	yield takeLatest(DELETE_SESSIONS, deleteSessions);
	yield takeLatest(SAVE_ATTENDANCE, saveAttendance);
	yield takeLatest(LOAD_PROGRAM, getProgram);
	yield takeLatest(GET_SESSIONS, getSessions);
	yield takeLatest(GET_CLIENTS, getClients);
	yield takeLatest(UPDATE_ENROLLMENT, updateEnrollment);
	yield takeLatest(FETCH_ALL_CLIENTS, fetchAllClients);
	yield takeLatest(ADD_CLIENT, addClient);
	yield takeLatest(SEND_SURVEY, sendSurvey);
	// See example in containers/HomePage/saga.js
}

export function* addClient(actions) {
	const { data, cb } = actions;
	try {
		const response = yield call(addNewClientAPI, data);
		if (response.response && response.response.status >= 400) {
			yield put(addClientError(response.response.data));
		} else {
			yield put(addClientSuccess(response.data.data));
			if (cb) {
				cb();
			}
		}
	} catch (err) {
		yield put(addClientError(err));
	}
}

export function* fetchAllClients(action) {
	try {
		const { role } = action;
		const api = role === 'admin' ? fetchAllClientsAPI : fetchMyClientsAPI;
		const response = yield call(api);

		if (response.response && response.response.status >= 400) {
			yield put(fetchAllClientsError());
		} else {
			yield put(fetchAllClientsSuccess(response.data));
		}
	} catch (err) {
		yield put(fetchAllClientsError(err));
	}
}

export function* getAttendance(actions) {
	const { data, cb = noop } = actions;
	try {
		const response = yield call(fetchAttendanceAPI, data);
		cb();
		yield put(getAttendanceSuccess(response));
	} catch (err) {
		yield put(getAttendanceError(err));
	}
}

export function* getProgram(actions) {
	try {
		const { data } = actions;
		const response = yield call(getProgramAPI, data);
		if (response.response && response.response.status >= 400) {
			yield put(loadProgramError());
		} else {
			yield put(loadProgramSuccess(response.data[0]));
		}
	} catch (err) {
		yield put(loadProgramError(err));
	}
}

export function* saveAttendance(actions) {
	const { data, res, errCB } = actions;
	try {
		const response = yield call(saveAttendanceAPI, data);
		yield put(saveAttendanceSuccess(response.data));
		if (res) {
			res();
		}
	} catch (err) {
		yield put(saveAttendanceError(err));
		if (errCB) {
			errCB();
		}
	}
}

export function* getSessions(actions) {
	const { data } = actions;
	try {
		const response = yield call(fetchSessionsAPI, data);
		yield put(getSessionsSuccess(response));
	} catch (err) {
		yield put(getSessionsError(err));
	}
}

export function* getClients(actions) {
	const { data } = actions;
	try {
		const response = yield call(fetchClientsAPI, data);
		yield put(getClientsSuccess(response.data));
	} catch (err) {
		yield put(getClientsError(err));
	}
}

/* export function* unenrollClient(actions) {
  const { data } = actions;
  try {
    const response = yield call(unenrollClientApi, data);
    if (response.response) {
      yield put(unenrollSuccess(response.response.data));
    } else {
      yield put(unenrollSuccess(response.data));
    }
  } catch (err) {
    yield put(unenrollError(err));
  }
} */

export function* updateEnrollment(actions) {
	const { data } = actions;
	try {
		const response = yield call(updateEnrollmentApi, data);
		yield put(updateEnrollmentSuccess(response.data));
		const { programId } = data;
		if (programId) {
			yield put(loadProgram({ id: programId }));
			// A save can add or remove instances, so the tab membership has to be
			// re-derived from the server rather than patched in the reducer.
			yield put(getClientsAction({ programId }));
		}
	} catch (err) {
		yield put(updateEnrollmentError(err));
	}
}

export function* addNewSession(actions) {
	const { data } = actions;
	try {
		const response = yield call(addSessionAPI, data);
		yield put(addSessionSuccess(response));
	} catch (err) {
		yield put(addSessionError(err));
	}
}

export function* deleteSessions(actions) {
	const { data } = actions;
	try {
		const response = yield call(deleteSessionsAPI, data);
		yield put(deleteSessionsSuccess(response));
	} catch (err) {
		yield put(deleteSessionsError(err));
	}
}

export function* sendSurvey(action) {
	const { data, successCB = noop, errCB = noop } = action;
	let response;
	try {
		// sendSurveyAPI swallows rejections (`.catch((err) => err)`), so a failed
		// send arrives as a *resolved* AxiosError rather than a throw. Only a
		// server-side failure carries `.response`; a network error, timeout or
		// CORS rejection carries neither that nor a status, so the old
		// `response.response && ...status >= 400` check fell straight through to
		// successCB and toasted "SMS Sent Successfully" over a send that never
		// left the browser. Treat any Error-shaped result as a failure.
		response = yield call(sendSurveyAPI, data);
		const status = response?.response?.status ?? response?.status;

		if (!response || response instanceof Error || status >= 400) {
			// `new Error('sendSurveyError', detail)` put a literal key in
			// err.message — Error's second argument is `options` ({cause}), not a
			// message part — and errCB renders err.message as the toast text, so
			// admins saw "sendSurveyError". getErrorMessage reads both
			// `data.message` and `data.error` (the codebase's documented
			// error-shape contract) instead of hand-rolling just `data.error`,
			// which also used to toast "[object Object]" for an object-valued
			// `data.error`.
			throw new Error(getErrorMessage(response));
		}
	} catch (err) {
		// The resolved AxiosError (status, response body) is the only
		// diagnostic trail for "SMS sending is broken" reports — the toast
		// auto-closes after 2.5s. Log the raw value, not just the message
		// string the toast shows.
		logger.error(response ?? err);
		try {
			// errCB renders a toast too (same shape of exposure as successCB
			// below): a throw here must not escape uncaught either.
			errCB(err.message);
		} catch (cbErr) {
			logger.error(cbErr);
		}
		return;
	}
	try {
		// successCB is called outside the try above on purpose: it renders a
		// toast, and folding it into that try would report a throw from the
		// toaster as a failed send — turning an SMS that actually went out into
		// an error toast. But the call must still be contained on its own:
		// under `takeLatest(SEND_SURVEY, sendSurvey)` + a single root
		// `all([...])` with no `onError` on the saga middleware, an uncaught
		// throw here cancels every saga in the app for the rest of the session
		// (buttons silently stop doing anything). Pre-PR the same throw was
		// caught above and misreported through errCB (wrong toast, app alive);
		// this trades that misleading toast for a logged no-op, without the
		// blast radius.
		successCB();
	} catch (err) {
		logger.error(err);
	}
}
